import type { ReactNode } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { api } from '../api/client'
import type { PkiCa, PkiCertificate, PkiConnection, PkiDeployment, PkiKey, PkiRecipe, PkiRequest, PkiTemplate } from '../api/types'
import { useAmministra } from '../components/BusinessApps'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { Connessioni } from '../components/Connessioni'
import type { Column } from '../components/DataTable'
import { DataTable } from '../components/DataTable'
import { AvvisiDelCertificato } from '../components/NotificheCertificati'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Sicurezza → Gestione certificati, with the Microsoft CA (AD CS).
 *
 * - Repository: the certificates the tenant manages (issued through CENSO,
 *   imported, synced from the CA database), with expiry and where they are
 *   installed;
 * - Richieste: the requests sent to the CA, with the state it gave them;
 * - Nuova richiesta: connection → CA and template → the commands that make
 *   the key and the CSR on the target machine → paste the CSR → send;
 * - CA e modelli: what each connection reaches.
 *
 * The private key never reaches CENSO. See pki_service and
 * docs/architecture/gestione-certificati.md.
 */
type Scheda = 'repository' | 'chiavi' | 'richieste' | 'nuova' | 'convertitore' | 'ca' | 'connessioni'

const SCHEDE: Array<{ key: Scheda; label: () => string }> = [
  { key: 'repository', label: () => t('Repository certificati') },
  { key: 'chiavi', label: () => t('Chiavi') },
  { key: 'richieste', label: () => t('Richieste') },
  { key: 'nuova', label: () => t('Nuova richiesta') },
  { key: 'convertitore', label: () => t('Convertitore') },
  { key: 'ca', label: () => t('CA e modelli') },
  { key: 'connessioni', label: () => t('Connessioni') },
]

const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(localeCorrente()) : '—')

function stato(c: { status: string; days_left: number | null }) {
  const g = c.days_left ?? 0
  if (c.status === 'expired') return <span className="badge badge--error">{t('scaduto da {n} giorni', { n: -g })}</span>
  if (c.status === 'expiring') return <span className="badge badge--warn">{t('scade fra {n} giorni', { n: g })}</span>
  if (c.status === 'soon') return <span className="badge badge--info">{t('scade fra {n} giorni', { n: g })}</span>
  if (c.status === 'ok') return <span className="badge badge--ok">{t('valido ancora {n} giorni', { n: g })}</span>
  return <span className="badge badge--muted">—</span>
}

const ORIGINE: Record<string, () => string> = {
  issued: () => t('emesso tramite CENSO'),
  import: () => t('importato'),
  ca: () => t('dal database della CA'),
}

const TIPO: Record<string, () => string> = {
  web: () => t('web'),
  computer: () => t('computer'),
  user: () => t('utente'),
  ca: () => t('CA'),
  code_signing: () => t('firma del codice'),
  other: () => t('altro'),
}

const STATO_RICHIESTA: Record<string, { label: () => string; tono: string }> = {
  draft: { label: () => t('bozza'), tono: 'muted' },
  pending: { label: () => t('in attesa di approvazione'), tono: 'warn' },
  issued: { label: () => t('emesso'), tono: 'ok' },
  denied: { label: () => t('rifiutato'), tono: 'error' },
  failed: { label: () => t('non riuscito'), tono: 'error' },
}

async function salva(blob: Blob, nome: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  a.click()
  URL.revokeObjectURL(url)
}

export default function GestioneCertificati() {
  const [params, setParams] = useSearchParams()
  const scheda = (SCHEDE.find((s) => s.key === params.get('tab'))?.key ?? 'repository') as Scheda
  const vai = (s: Scheda, extra: Record<string, string> = {}) => setParams({ tab: s, ...extra })
  const connessioni = useAsync(() => api.pkiConnections(), [])

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Gestione certificati')}</h1>
          <p className="page__subtitle">
            {t('gestisce l’intero ciclo di richiesta dei certificati tramite Microsoft AD CS, dalla preparazione alla generazione e al download. La chiave privata può essere generata sulla macchina di destinazione o conservata cifrata in CENSO.')}
            {' '}<Link to="/encryption/certificates">{t('I certificati che le macchine espongono sono in Anagrafica certificati.')}</Link>
          </p>
        </div>
      </header>
      <nav className="tabs" role="tablist">
        {SCHEDE.map((s) => (
          <button key={s.key} type="button" role="tab" aria-selected={scheda === s.key}
            className={`tabs__tab${scheda === s.key ? ' tabs__tab--active' : ''}`} onClick={() => vai(s.key)}>
            {s.label()}
          </button>
        ))}
      </nav>
      <ErrorBanner error={connessioni.error} />
      {connessioni.data && connessioni.data.length === 0 && ['richieste', 'nuova', 'ca'].includes(scheda) && (
        <Card>
          <EmptyState title={t('Nessuna connessione a una CA Microsoft')}
            hint={t('Aggiungi una connessione «CA Microsoft (AD CS)» nella scheda «Connessioni» — WinRM verso un server Windows del dominio oppure il sito Web Enrollment (/certsrv) — e abilita la capability «PKI (CA Microsoft)» sulla sonda che raggiunge la CA.')} />
        </Card>
      )}
      {scheda === 'repository' && <Repository connessioni={connessioni.data ?? []} onRinnova={(c) => vai('nuova', {
        cn: c.cn, sans: c.sans.filter((s) => !s.startsWith('email:')).join(','), ...(c.ci_id ? { ci: c.ci_id } : {}),
        replaces: c.sha256,
      })} />}
      {scheda === 'chiavi' && <Chiavi />}
      {scheda === 'convertitore' && <Convertitore />}
      {scheda === 'richieste' && <Richieste connessioni={connessioni.data ?? []} onNuova={() => vai('nuova')} />}
      {scheda === 'nuova' && (connessioni.data?.length ?? 0) > 0 && (
        <NuovaRichiesta connessioni={connessioni.data!} iniziale={{
          cn: params.get('cn') ?? '', sans: params.get('sans') ?? '', ci: params.get('ci'), replaces: params.get('replaces'),
        }} onCreata={() => vai('richieste')} />
      )}
      {scheda === 'ca' && (connessioni.data?.length ?? 0) > 0 && <CaEModelli connessioni={connessioni.data!} />}
      {scheda === 'connessioni' && <Connessioni funzioni={['pki']} segnaposto="ca-aziendale" onChanged={() => void connessioni.reload()} />}
    </>
  )
}

// ------------------------------------------------------------ repository

function Repository({ connessioni, onRinnova }: { connessioni: PkiConnection[]; onRinnova: (c: PkiCertificate) => void }) {
  const repo = useAsync(() => api.pkiCertificates(), [])
  const [filtro, setFiltro] = useState<'tutti' | 'scaduti' | 'scadenza' | 'installati'>('tutti')
  // Web certificates first: those are the ones usually looked after; the
  // computer and user ones of auto-enrollment are many more.
  const [altri, setAltri] = useState(false)
  const [aperto, setAperto] = useState<PkiCertificate | null>(null)
  const [importa, setImporta] = useState(false)
  const [sincronizza, setSincronizza] = useState(false)
  const d = repo.data
  const vista = (d?.items ?? []).filter((c) => (c.cert_type === 'web') !== altri)
  const righe = vista.filter((c) => filtro === 'tutti' || (filtro === 'scaduti' ? c.status === 'expired'
    : filtro === 'scadenza' ? c.status === 'expiring' || c.status === 'soon' : c.installed_on.length > 0))
  const conta = (f: (c: PkiCertificate) => boolean) => vista.filter(f).length

  const colonne: Column<PkiCertificate>[] = [
    { key: 'cn', header: t('Certificato'), value: (c) => `${c.cn} ${c.sans.join(' ')}`, render: (c) => (
      <>
        <button type="button" className="link" style={{ fontWeight: 600, textAlign: 'left' }} onClick={() => setAperto(c)}>{c.cn || c.subject || '—'}</button>
        {c.sans.length > 0 && <><br /><small className="text-muted">{c.sans.slice(0, 3).join(', ')}{c.sans.length > 3 ? ` +${c.sans.length - 3}` : ''}</small></>}
        {c.is_ca && <> <span className="badge badge--info">CA</span></>}
      </>
    ) },
    { key: 'cert_type', header: t('Tipo'), value: (c) => TIPO[c.cert_type]?.() ?? c.cert_type, render: (c) => (
      <span title={c.eku.join(', ')}>{TIPO[c.cert_type]?.() ?? c.cert_type}</span>
    ) },
    { key: 'issuer', header: t('Emittente (CA)'), value: (c) => c.issuer, render: (c) => <small>{c.self_signed ? t('autofirmato') : c.issuer.replace(/^CN=/, '').split(',')[0]}</small> },
    { key: 'days_left', header: t('Scadenza'), value: (c) => c.days_left ?? 99999, filter: false, render: (c) => (
      <>{stato(c)}<br /><small className="text-muted">{data(c.not_after)}</small></>
    ) },
    { key: 'source', header: t('Origine'), value: (c) => ORIGINE[c.source]?.() ?? c.source, render: (c) => (
      <><small>{ORIGINE[c.source]?.() ?? c.source}</small>{c.template && <><br /><small className="text-muted">{c.template}</small></>}</>
    ) },
    { key: 'installed', header: t('Installato su'), value: (c) => c.installed_on.map((i) => i.ci_name).join(' '), render: (c) => (
      c.installed_on.length === 0 ? <span className="text-muted">—</span> : <>{c.installed_on.slice(0, 3).map((i) => (
        <div key={`${i.ci_id}:${i.port}`}><Link to={`/cis/${i.ci_id}`}>{i.ci_name}</Link> <small className="mono text-muted">:{i.port}</small></div>
      ))}{c.installed_on.length > 3 && <small className="text-muted">+{c.installed_on.length - 3}</small>}</>
    ) },
    { key: 'has_key', header: t('Chiave'), value: (c) => (c.has_key ? t('nello store') : '—'), render: (c) => (
      c.has_key ? <span className="badge badge--ok" title={t('La chiave privata è nello store, cifrata')}>{t('nello store')}</span>
        : <span className="text-muted">—</span>
    ) },
    { key: 'owner', header: t('Responsabile'), value: (c) => c.owner ?? '' },
  ]

  return (
    <>
      <ErrorBanner error={repo.error} />
      <div className="toolbar" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
        <button type="button" className="secondary" onClick={() => setImporta(true)}>{t('Importa certificato')}</button>
        {connessioni.some((c) => c.can_sync) && (
          <button type="button" className="secondary" onClick={() => setSincronizza(true)}>{t('Leggi i certificati emessi dalla CA')}</button>
        )}
      </div>
      {repo.loading && !d ? <Spinner /> : d && (
        <>
          <div className="segmented" role="tablist" aria-label={t('Tipo')}>
            <button type="button" role="tab" aria-selected={!altri} className={!altri ? 'segmented__on' : ''}
              onClick={() => { setAltri(false); setFiltro('tutti') }}>
              {t('Certificati web ({n})', { n: d.by_type.web ?? 0 })}
            </button>
            <button type="button" role="tab" aria-selected={altri} className={altri ? 'segmented__on' : ''}
              onClick={() => { setAltri(true); setFiltro('tutti') }}>
              {t('Altri certificati ({n})', { n: d.total - (d.by_type.web ?? 0) })}
            </button>
          </div>
          <div className="stats">
            {([
              ['tutti', vista.length, altri ? t('Altri certificati') : t('Certificati web'), ''],
              ['scaduti', conta((c) => c.status === 'expired'), t('Scaduti'), conta((c) => c.status === 'expired') ? 'error' : 'ok'],
              ['scadenza', conta((c) => c.status === 'expiring'), t('Scadono entro 30 giorni'), conta((c) => c.status === 'expiring') ? 'warn' : 'ok'],
              ['installati', conta((c) => c.installed_on.length > 0), t('Visti installati dall’agente'), ''],
            ] as const).map(([k, n, label, tono]) => (
              <button key={k} type="button" className={`stat${tono ? ` stat--${tono}` : ''}`} aria-pressed={filtro === k}
                onClick={() => setFiltro(k)}
                style={filtro === k ? { borderColor: 'var(--accent)', boxShadow: 'var(--shadow), 0 0 0 1px var(--accent)' } : undefined}>
                <div className="stat__value">{n.toLocaleString(localeCorrente())}</div>
                <div className="stat__label">{label}</div>
              </button>
            ))}
          </div>
          <Card>
            {righe.length === 0 ? (
              <EmptyState title={t('Nessun certificato nel repository')}
                hint={t('Entrano da sé quando la CA emette una richiesta fatta da qui; si importano con «Importa certificato» o si leggono dal database della CA (connessione WinRM).')} />
            ) : (
              <DataTable rows={righe} rowKey={(c) => c.id} columns={colonne} initialSort={{ key: 'days_left', dir: 'asc' }}
                conteggio={{ nome: t('certificati') }} paginazione={{ predefinita: 50 }} />
            )}
          </Card>
        </>
      )}
      {aperto && <DettaglioRepository c={aperto} onChiudi={() => setAperto(null)} onCambiato={() => { setAperto(null); void repo.reload() }}
        onRinnova={() => onRinnova(aperto)} />}
      {importa && <Importa onChiudi={() => setImporta(false)} onFatto={() => { setImporta(false); void repo.reload() }} />}
      {sincronizza && <Sincronizza connessioni={connessioni.filter((c) => c.can_sync)} onChiudi={() => setSincronizza(false)}
        onFatto={() => { setSincronizza(false); void repo.reload() }} />}
    </>
  )
}

function Finestra({ titolo, sotto, onChiudi, children, larghezza = 760 }: {
  titolo: string; sotto?: string; onChiudi: () => void; children: ReactNode; larghezza?: number
}) {
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onChiudi() }
    window.addEventListener('keydown', esc)
    return () => window.removeEventListener('keydown', esc)
  }, [onChiudi])
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={titolo}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello" style={{ width: `min(${larghezza}px, 96vw)`, maxHeight: '90vh' }}>
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{titolo}</h3>
            {sotto && <p className="dob-modal__sotto">{sotto}</p>}
          </div>
          <div className="dob-modal__comandi"><button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button></div>
        </div>
        <div className="dob-modal__corpo">{children}</div>
      </div>
    </div>
  )
}

function Riga({ etichetta, children }: { etichetta: string; children: ReactNode }) {
  return (
    <tr>
      <th style={{ textAlign: 'left', fontWeight: 500, color: 'var(--text-muted)', width: 170, verticalAlign: 'top' }}>{etichetta}</th>
      <td style={{ wordBreak: 'break-word' }}>{children}</td>
    </tr>
  )
}

function DettaglioRepository({ c, onChiudi, onCambiato, onRinnova }: {
  c: PkiCertificate; onChiudi: () => void; onCambiato: () => void; onRinnova: () => void
}) {
  const [owner, setOwner] = useState(c.owner ?? '')
  const [notes, setNotes] = useState(c.notes ?? '')
  const [errore, setErrore] = useState<unknown>(null)
  const nome = (c.cn || c.sha256.slice(0, 16)).replace(/[^A-Za-z0-9.-]/g, '_').replace('*', 'wildcard')
  const catena = useAsync(() => api.pkiCertificate(c.id), [c.id])
  const admin = useAmministra()
  async function scarica(formato: 'pem' | 'der' | 'fullchain' | 'cachain' | 'p7b', file: string) {
    setErrore(null)
    try { await salva(await api.pkiDownload(c.id, formato), file) } catch (e) { setErrore(e) }
  }
  async function salvaDati() {
    try { await api.pkiUpdateCertificate(c.id, { owner, notes }); onCambiato() } catch (e) { setErrore(e) }
  }
  async function elimina() {
    if (!window.confirm(t('Togliere il certificato dal repository? Non viene revocato sulla CA.'))) return
    try { await api.pkiDeleteCertificate(c.id); onCambiato() } catch (e) { setErrore(e) }
  }
  return (
    <Finestra titolo={c.cn || t('Certificato')} sotto={ORIGINE[c.source]?.()} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <div className="toolbar" style={{ gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {stato(c)}

        {!c.is_ca && <button type="button" onClick={onRinnova}>{t('Rinnova con la CA')}</button>}
      </div>
      <table><tbody>
        <Riga etichetta={t('Soggetto')}><span className="mono">{c.subject}</span></Riga>
        <Riga etichetta={t('Nomi alternativi (SAN)')}>{c.sans.length ? <span className="mono">{c.sans.join(', ')}</span> : '—'}</Riga>
        <Riga etichetta={t('Emittente (CA)')}><span className="mono">{c.issuer}</span></Riga>
        <Riga etichetta={t('Numero di serie')}><span className="mono">{c.serial}</span></Riga>
        <Riga etichetta={t('Valido dal')}>{data(c.not_before)}</Riga>
        <Riga etichetta={t('Valido fino al')}>{data(c.not_after)}</Riga>
        <Riga etichetta={t('Chiave')}>{c.key_type}{c.key_bits ? ` ${c.key_bits} bit` : ''} · {c.signature}</Riga>
        <Riga etichetta={t('Tipo')}>{TIPO[c.cert_type]?.() ?? c.cert_type}{c.eku.length > 0 && <small className="text-muted"> · {c.eku.join(', ')}</small>}</Riga>
        {c.template && <Riga etichetta={t('Modello')}>{c.template}{c.ca_request_id ? ` · ${t('richiesta n. {n}', { n: c.ca_request_id })}` : ''}</Riga>}
        {c.ca_config && <Riga etichetta={t('CA')}><span className="mono">{c.ca_config}</span></Riga>}
        {c.requester && <Riga etichetta={t('Richiedente')}>{c.requester}</Riga>}
        <Riga etichetta={t('Catena')}>
          {catena.data ? (
            <>
              {catena.data.chain.length === 0 ? <small className="text-muted">{t('nessun certificato della CA nel repository')}</small>
                : catena.data.chain.map((x) => <div key={x.id}><small>↳ {x.cn}{x.not_after ? ` · ${t('valido fino al {d}', { d: data(x.not_after) })}` : ''}</small></div>)}
              {!catena.data.complete && <small style={{ color: 'var(--warn)' }}>{t('Catena incompleta: apri «CA e modelli» per far entrare il certificato della CA, o importalo.')}</small>}
            </>
          ) : '…'}
        </Riga>
        <Riga etichetta={t('Installato su')}>{c.installed_on.length === 0 ? <small className="text-muted">{t('l’agente CENSO non lo vede esposto da nessuna parte')}</small>
          : c.installed_on.map((i) => <div key={`${i.ci_id}:${i.port}`}><Link to={`/cis/${i.ci_id}`}>{i.ci_name}</Link> <span className="mono text-muted">:{i.port}</span>{i.process ? ` · ${i.process}` : ''}</div>)}</Riga>
        {c.installed_on.length === 0 && (
          <Riga etichetta={t('Avvisi inviati')}><AvvisiDelCertificato chiave={`pki:${c.id}:${c.sha256}`} /></Riga>
        )}
        <Riga etichetta={t('Impronta SHA-256')}><span className="mono" style={{ fontSize: 12 }}>{c.sha256}</span></Riga>
        <Riga etichetta={t('Impronta SHA-1')}><span className="mono" style={{ fontSize: 12 }}>{c.sha1}</span></Riga>
      </tbody></table>
      <h4 style={{ margin: '14px 0 4px' }}>{t('Scarica')}</h4>
      <div className="toolbar" style={{ gap: 6, flexWrap: 'wrap' }}>
        <button type="button" className="secondary" onClick={() => void scarica('pem', `${nome}.pem`)}>{t('Certificato .pem')}</button>
        <button type="button" className="secondary" onClick={() => void scarica('der', `${nome}.cer`)}>{t('Certificato .cer (DER)')}</button>
        <button type="button" className="secondary" onClick={() => void scarica('fullchain', `${nome}-fullchain.pem`)}>{t('Con la catena .pem')}</button>
        <button type="button" className="secondary" onClick={() => void scarica('cachain', `${nome}-ca-chain.pem`)}>{t('Solo le CA .pem')}</button>
        <button type="button" className="secondary" onClick={() => void scarica('p7b', `${nome}.p7b`)}>{t('Catena .p7b (PKCS#7)')}</button>
      </div>
      <p className="field__hint" style={{ marginTop: 4 }}>
        {t('«Con la catena» è quello che vuole un server (nginx, Apache, HAProxy); «Solo le CA» è il bundle da dare ai client; il .p7b si importa così in Windows.')}
      </p>
      {c.has_key && <Esporta c={c} admin={admin} nome={nome} />}
      {c.has_key && admin && <Installa c={c} />}
      <h4 style={{ margin: '14px 0 4px' }}>{t('Gestione')}</h4>
      <label className="field"><span className="field__label">{t('Responsabile')}</span>
        <input type="text" value={owner} onChange={(e) => setOwner(e.target.value)} maxLength={200} /></label>
      <label className="field"><span className="field__label">{t('Note')}</span>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={4000} /></label>
      <div className="toolbar" style={{ gap: 8 }}>
        <button type="button" onClick={salvaDati}>{t('Salva')}</button>
        <button type="button" className="secondary" onClick={elimina}>{t('Togli dal repository')}</button>
      </div>
    </Finestra>
  )
}

function aBase64(buf: Uint8Array): string {
  let bin = ''
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000))
  return btoa(bin)
}

/** A file as text: PEM as it is, any binary (DER, P7B, PFX) as base64. */
async function leggiFileCertificato(f: File): Promise<string> {
  const buf = new Uint8Array(await f.arrayBuffer())
  const txt = new TextDecoder().decode(buf)
  return txt.includes('-----BEGIN') ? txt : aBase64(buf)
}

function Importa({ onChiudi, onFatto }: { onChiudi: () => void; onFatto: () => void }) {
  const [testo, setTesto] = useState('')
  const [file, setFile] = useState('')
  const [password, setPassword] = useState('')
  const [owner, setOwner] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [esito, setEsito] = useState<string | null>(null)
  async function importa() {
    setErrore(null)
    try {
      const r = await api.pkiImport({ text: testo, password: password || null, owner: owner || null })
      setEsito(t('{n} certificati importati, {k} chiavi nello store', { n: r.imported, k: r.keys }))
      setTimeout(onFatto, 1200)
    } catch (e) { setErrore(e) }
  }
  return (
    <Finestra titolo={t('Importa certificato')} sotto={t('PEM, .cer/.crt/.der, .p7b, oppure .pfx/.p12 con la sua password: i certificati vanno nel repository, la chiave privata (se c’è) nello store, cifrata con la chiave del tenant.')} onChiudi={onChiudi}>
      <ErrorBanner error={errore} />
      <input type="file" accept=".pem,.cer,.crt,.der,.p7b,.p7c,.pfx,.p12,.key"
        onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setFile(f.name); setTesto(await leggiFileCertificato(f)) } }} />
      {file && <small className="text-muted"> {file}</small>}
      <label className="field"><span className="field__label">{t('Oppure incolla il testo')}</span>
        <textarea className="mono" rows={8} value={testo} onChange={(e) => { setTesto(e.target.value); setFile('') }} placeholder="-----BEGIN CERTIFICATE-----" /></label>
      <label className="field"><span className="field__label">{t('Password del file (PFX o chiave cifrata)')}</span>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" /></label>
      <label className="field"><span className="field__label">{t('Responsabile')}</span>
        <input type="text" value={owner} onChange={(e) => setOwner(e.target.value)} maxLength={200} /></label>
      {esito && <p><strong>{esito}</strong></p>}
      <button type="button" disabled={!testo.trim()} onClick={importa}>{t('Importa')}</button>
    </Finestra>
  )
}

/** The certificate with its private key: administrators, a password chosen now. */
function Esporta({ c, admin, nome }: { c: PkiCertificate; admin: boolean; nome: string }) {
  const [formato, setFormato] = useState<'pfx' | 'pfx_legacy' | 'key_pem'>('pfx')
  const [password, setPassword] = useState('')
  const [conferma, setConferma] = useState('')
  const [catena, setCatena] = useState(true)
  const [errore, setErrore] = useState<unknown>(null)
  if (!admin) {
    return <p className="field__hint">{t('La chiave privata è nello store: la può esportare (PFX o PEM cifrata) un amministratore del tenant.')}</p>
  }
  async function esporta() {
    setErrore(null)
    try {
      const blob = await api.pkiExport(c.id, { format: formato, password, include_chain: catena })
      await salva(blob, formato === 'key_pem' ? `${nome}.key.pem` : `${nome}.pfx`)
      setPassword(''); setConferma('')
    } catch (e) { setErrore(e) }
  }
  return (
    <>
      <h4 style={{ margin: '14px 0 4px' }}>{t('Con la chiave privata')}</h4>
      <ErrorBanner error={errore} />
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="field"><span className="field__label">{t('Formato')}</span>
          <select value={formato} onChange={(e) => setFormato(e.target.value as typeof formato)}>
            <option value="pfx">{t('PFX / P12 (AES)')}</option>
            <option value="pfx_legacy">{t('PFX compatibile (3DES, Windows prima del 2019)')}</option>
            <option value="key_pem">{t('Chiave .pem cifrata (PKCS#8)')}</option>
          </select></label>
        <label className="field"><span className="field__label">{t('Password (almeno 8 caratteri)')}</span>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></label>
        <label className="field"><span className="field__label">{t('Ripeti la password')}</span>
          <input type="password" value={conferma} onChange={(e) => setConferma(e.target.value)} autoComplete="new-password" /></label>
        {formato !== 'key_pem' && (
          <label className="check"><input type="checkbox" checked={catena} onChange={(e) => setCatena(e.target.checked)} /> {t('Con la catena delle CA')}</label>
        )}
        <button type="button" disabled={password.length < 8 || password !== conferma} onClick={esporta}>{t('Esporta')}</button>
      </div>
      <p className="field__hint">{t('Ogni esportazione finisce nell’audit del tenant e nel conteggio della chiave. Conserva la password a parte: senza, il file non si apre.')}</p>
    </>
  )
}

/** The services the agent may reload after writing the files (product names). */
const SERVIZI_RICARICABILI = ['nginx', 'apache2', 'httpd', 'haproxy']

const STATO_INSTALLAZIONE: Record<string, { label: () => string; tono: string }> = {
  queued: { label: () => t('in attesa dell’agente'), tono: 'muted' },
  delivered: { label: () => t('consegnato all’agente'), tono: 'info' },
  installed: { label: () => t('installato'), tono: 'ok' },
  failed: { label: () => t('non riuscito'), tono: 'error' },
  cancelled: { label: () => t('annullato'), tono: 'muted' },
}

/** Install the certificate with its key on a machine through its CENSO agent. */
function Installa({ c }: { c: PkiCertificate }) {
  const destinazioni = useAsync(() => api.pkiDeployTargets(), [])
  const storico = useAsync(() => api.pkiDeployments(c.id), [c.id])
  const preferito = c.installed_on[0]?.ci_id ?? c.ci_id ?? ''
  const [ci, setCi] = useState(preferito)
  const [iis, setIis] = useState('')
  const [porta, setPorta] = useState('443')
  const [winrm, setWinrm] = useState(false)
  const nomeFile = (c.cn || 'certificato').replace('*', 'wildcard').replace(/[^A-Za-z0-9.-]/g, '_')
  const [certPath, setCertPath] = useState(`/etc/ssl/certs/${nomeFile}.crt`)
  const [keyPath, setKeyPath] = useState(`/etc/ssl/private/${nomeFile}.key`)
  const [unico, setUnico] = useState('')
  const [ricarica, setRicarica] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  useEffect(() => { if (!ci && destinazioni.data?.length) setCi(destinazioni.data[0].ci_id) }, [ci, destinazioni.data])
  const scelta = destinazioni.data?.find((d) => d.ci_id === ci)
  // While an installation waits for the agent, the list refreshes by itself.
  useEffect(() => {
    if (!(storico.data ?? []).some((d) => d.status === 'queued' || d.status === 'delivered')) return
    const id = window.setInterval(() => void storico.reload(), 15000)
    return () => window.clearInterval(id)
  }, [storico])

  async function installa() {
    if (!scelta) return
    setLavoro(true); setErrore(null)
    const target = scelta.platform === 'windows'
      ? { iis_site: iis || undefined, iis_port: Number(porta) || 443, winrm }
      : { cert_path: unico ? undefined : certPath, key_path: unico ? undefined : keyPath, combined_path: unico || undefined, reload: ricarica }
    try { await api.pkiDeploy(c.id, { ci_id: scelta.ci_id, target }); await storico.reload() } catch (e) { setErrore(e) } finally { setLavoro(false) }
  }
  async function azione(f: () => Promise<unknown>) {
    setErrore(null)
    try { await f(); await storico.reload() } catch (e) { setErrore(e) }
  }
  const statoInstallazione = (d: PkiDeployment) => STATO_INSTALLAZIONE[d.status]?.label() ?? d.status
  const colonneStorico: Column<PkiDeployment>[] = [
    { key: 'agent', header: t('Agente'), value: (d) => `${d.agent ?? ''} ${d.requested_by ?? ''}`.trim(), render: (d) => (
      <><small>{d.agent ?? '—'}</small><br /><small className="text-muted">{data(d.created_at)} · {d.requested_by}</small></>
    ) },
    { key: 'status', header: t('Stato'), value: statoInstallazione, render: (d) => (
      <><span className={`badge badge--${STATO_INSTALLAZIONE[d.status]?.tono ?? 'muted'}`}>{statoInstallazione(d)}</span>
        {d.message && <><br /><small className="text-muted">{d.message}</small></>}</>
    ) },
    { key: 'azioni', header: '', sort: false, filter: false, searchable: false, nowrap: true, render: (d) => (
      <>
        {['failed', 'delivered', 'cancelled'].includes(d.status) && <button type="button" className="link" onClick={() => void azione(() => api.pkiRetryDeployment(d.id))}>{t('Ripeti')}</button>}{' '}
        {['queued', 'delivered', 'failed'].includes(d.status) && <button type="button" className="link" onClick={() => void azione(() => api.pkiCancelDeployment(d.id))}>{t('Annulla')}</button>}
      </>
    ) },
  ]
  return (
    <>
      <h4 style={{ margin: '14px 0 4px' }}>{t('Installa sul server')}</h4>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('L’agente CENSO della macchina ritira certificato, catena e chiave cifrati per lui al prossimo contatto (entro un minuto) e li installa. Fa solo quello che scegli qui: nessun comando arriva da fuori.')}
      </p>
      <ErrorBanner error={errore ?? destinazioni.error} />
      {destinazioni.data && destinazioni.data.length === 0 ? (
        <p className="text-muted"><small>{t('Nessun CI con un agente CENSO attivo su Windows o Linux.')}</small></p>
      ) : (
        <>
          <label className="field"><span className="field__label">{t('Server')}</span>
            <select value={ci} onChange={(e) => setCi(e.target.value)}>
              {(destinazioni.data ?? []).map((d) => <option key={d.ci_id} value={d.ci_id}>{d.ci_name} · {d.agent} · {d.platform === 'windows' ? 'Windows' : 'Linux'}</option>)}
            </select></label>
          {scelta?.platform === 'windows' && (
            <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <small className="text-muted" style={{ flexBasis: '100%' }}>{t('Sempre: importazione nell’archivio certificati del computer (LocalMachine\\My), chiave non esportabile.')}</small>
              <label className="field"><span className="field__label">{t('Sito IIS (facoltativo)')}</span>
                <input type="text" value={iis} onChange={(e) => setIis(e.target.value)} placeholder={t('es. Default Web Site')} /></label>
              <label className="field"><span className="field__label">{t('Porta HTTPS')}</span>
                <input type="text" value={porta} onChange={(e) => setPorta(e.target.value)} style={{ width: 80 }} /></label>
              <label className="check"><input type="checkbox" checked={winrm} onChange={(e) => setWinrm(e.target.checked)} /> {t('Usalo per il listener WinRM HTTPS')}</label>
            </div>
          )}
          {scelta?.platform === 'linux' && (
            <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <label className="field" style={{ flex: '1 1 100%' }}><span className="field__label">{t('Certificato con la catena')}</span>
                <input type="text" value={certPath} onChange={(e) => setCertPath(e.target.value)} disabled={Boolean(unico)} /></label>
              <label className="field" style={{ flex: '1 1 100%' }}><span className="field__label">{t('Chiave privata')}</span>
                <input type="text" value={keyPath} onChange={(e) => setKeyPath(e.target.value)} disabled={Boolean(unico)} /></label>
              <label className="field" style={{ flex: '1 1 100%' }}><span className="field__label">{t('Oppure file unico (HAProxy)')}</span>
                <input type="text" value={unico} onChange={(e) => setUnico(e.target.value)} placeholder={t('es. /etc/haproxy/certs/sito.pem')} /></label>
              <label className="field"><span className="field__label">{t('Poi ricarica')}</span>
                <select value={ricarica} onChange={(e) => setRicarica(e.target.value)}>
                  <option value="">{t('niente')}</option>
                  {SERVIZI_RICARICABILI.map((x) => <option key={x} value={x}>{x}</option>)}
                </select></label>
              <small className="text-muted" style={{ flexBasis: '100%' }}>{t('Solo sotto /etc/ssl, /etc/pki, /etc/nginx, /etc/apache2, /etc/httpd, /etc/haproxy, /etc/censo, /opt, /srv; i file che c’erano restano con il suffisso .censo-bak; la chiave è leggibile solo da root.')}</small>
            </div>
          )}
          <button type="button" style={{ marginTop: 8 }} disabled={!scelta || lavoro} onClick={installa}>{t('Installa')}</button>
        </>
      )}
      {(storico.data ?? []).length > 0 && (
        // A certificate's few installations, inside its window: no toolbar.
        <div style={{ marginTop: 10 }}>
          <DataTable rows={storico.data!} columns={colonneStorico} rowKey={(d) => d.id} strumenti={false} />
        </div>
      )}
    </>
  )
}

// ------------------------------------------------------------ key store

function Chiavi() {
  const lista = useAsync(() => api.pkiKeys(), [])
  const admin = useAmministra()
  const [errore, setErrore] = useState<unknown>(null)
  async function elimina(k: PkiKey) {
    if (!window.confirm(t('Eliminare la chiave dallo store? I certificati restano, ma senza chiave non si potranno più esportare in PFX da qui.'))) return
    try { await api.pkiDeleteKey(k.id); await lista.reload() } catch (e) { setErrore(e) }
  }
  const colonne: Column<PkiKey>[] = [
    { key: 'label', header: t('Chiave'), value: (k) => `${k.label ?? ''} ${k.key_type}`, render: (k) => (
      <><strong>{k.label || k.spki_sha256.slice(0, 16)}</strong><br /><small className="text-muted">{k.key_type}{k.key_bits ? ` ${k.key_bits}` : ''}</small></>
    ) },
    { key: 'source', header: t('Origine'), value: (k) => (k.source === 'generated' ? t('generata in CENSO') : t('importata')) },
    { key: 'certificates', header: t('Certificati'), value: (k) => k.certificates.map((c) => c.cn).join(' '), render: (k) => (
      k.certificates.length === 0 && k.requests.length === 0 ? <span className="text-muted">{t('nessuno')}</span> : <>
        {k.certificates.map((c) => <div key={c.id}><small>{c.cn}{c.not_after ? ` · ${data(c.not_after)}` : ''}</small></div>)}
        {k.requests.filter((r) => r.status !== 'issued').map((r) => <div key={r.id}><small className="text-muted">{t('richiesta')}: {r.cn} · {STATO_RICHIESTA[r.status]?.label() ?? r.status}</small></div>)}
      </>
    ) },
    { key: 'created_at', header: t('Creata'), value: (k) => k.created_at ?? '', filter: false, render: (k) => (
      <><small>{data(k.created_at)}</small>{k.created_by && <><br /><small className="text-muted">{k.created_by}</small></>}</>
    ) },
    { key: 'export_count', header: t('Esportazioni'), value: (k) => k.export_count, filter: false, render: (k) => (
      <><small>{k.export_count}</small>{k.last_exported_at && <><br /><small className="text-muted">{data(k.last_exported_at)} · {k.last_exported_by}</small></>}</>
    ) },
    { key: 'azioni', header: '', filter: false, sort: false, render: (k) => (admin
      ? <button type="button" className="link" onClick={() => void elimina(k)}>{t('Elimina')}</button> : null) },
  ]
  return (
    <>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Chiavi private archiviate nello store cifrato con la chiave del tenant e protette dal relativo ciclo di rotazione. Non sono mai esposte in chiaro; l’esportazione è consentita solo agli amministratori insieme al certificato, in PFX o PEM protetto da password.')}
      </p>
      <ErrorBanner error={errore ?? lista.error} />
      {lista.loading && !lista.data ? <Spinner /> : (
        <Card>
          {(lista.data ?? []).length === 0 ? <EmptyState title={t('Nessuna chiave nello store')} /> : (
            <DataTable rows={lista.data!} rowKey={(k) => k.id} columns={colonne} initialSort={{ key: 'created_at', dir: 'desc' }}
              conteggio={{ nome: t('chiavi') }} paginazione={{ predefinita: 50 }} />
          )}
        </Card>
      )}
    </>
  )
}

// ------------------------------------------------------------ converter

function Convertitore() {
  const [testo, setTesto] = useState('')
  const [file, setFile] = useState('')
  const [pin, setPin] = useState('')
  const [formato, setFormato] = useState('fullchain')
  const [pout, setPout] = useState('')
  const [catena, setCatena] = useState(true)
  const [errore, setErrore] = useState<unknown>(null)
  const conChiave = ['pfx', 'pfx_legacy', 'key_pem'].includes(formato)
  const ESTENSIONE: Record<string, string> = {
    pem: '.pem', fullchain: '-fullchain.pem', der: '.cer', p7b: '.p7b', pfx: '.pfx', pfx_legacy: '.pfx', key_pem: '.key.pem',
  }
  async function converti() {
    setErrore(null)
    try {
      const blob = await api.pkiConvert({ text: testo, target: formato, password_in: pin || null, password_out: pout || null, include_chain: catena })
      const base = (file.replace(/\.[^.]+$/, '') || 'certificato').replace(/[^A-Za-z0-9.-]/g, '_')
      await salva(blob, `${base}${ESTENSIONE[formato]}`)
    } catch (e) { setErrore(e) }
  }
  return (
    <Card title={t('Convertitore di certificati')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Carica un certificato in qualsiasi formato — PEM, .cer/.crt/.der, .p7b, .pfx/.p12 — e scaricalo in un altro. Non salva niente nel repository né nello store: legge, converte e restituisce il file.')}
      </p>
      <ErrorBanner error={errore} />
      <input type="file" accept=".pem,.cer,.crt,.der,.p7b,.p7c,.pfx,.p12,.key"
        onChange={async (e) => { const f = e.target.files?.[0]; if (f) { setFile(f.name); setTesto(await leggiFileCertificato(f)) } }} />
      {file && <small className="text-muted"> {file}</small>}
      <label className="field"><span className="field__label">{t('Oppure incolla il testo')}</span>
        <textarea className="mono" rows={6} value={testo} onChange={(e) => { setTesto(e.target.value); setFile('') }} /></label>
      <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label className="field"><span className="field__label">{t('Password del file (se PFX o chiave cifrata)')}</span>
          <input type="password" value={pin} onChange={(e) => setPin(e.target.value)} autoComplete="off" /></label>
        <label className="field"><span className="field__label">{t('Converti in')}</span>
          <select value={formato} onChange={(e) => setFormato(e.target.value)}>
            <option value="pem">{t('Certificato .pem')}</option>
            <option value="fullchain">{t('Con la catena .pem')}</option>
            <option value="der">{t('Certificato .cer (DER)')}</option>
            <option value="p7b">{t('Catena .p7b (PKCS#7)')}</option>
            <option value="pfx">{t('PFX / P12 (AES)')}</option>
            <option value="pfx_legacy">{t('PFX compatibile (3DES, Windows prima del 2019)')}</option>
            <option value="key_pem">{t('Solo la chiave .pem')}</option>
          </select></label>
        {conChiave && (
          <label className="field"><span className="field__label">{formato === 'key_pem' ? t('Password della chiave (vuota: in chiaro)') : t('Password del nuovo PFX')}</span>
            <input type="password" value={pout} onChange={(e) => setPout(e.target.value)} autoComplete="new-password" /></label>
        )}
        {formato !== 'pem' && formato !== 'der' && formato !== 'key_pem' && (
          <label className="check"><input type="checkbox" checked={catena} onChange={(e) => setCatena(e.target.checked)} /> {t('Con la catena delle CA')}</label>
        )}
        <button type="button" disabled={!testo.trim() || (formato.startsWith('pfx') && pout.length < 8)} onClick={converti}>{t('Converti e scarica')}</button>
      </div>
    </Card>
  )
}

function Sincronizza({ connessioni, onChiudi, onFatto }: { connessioni: PkiConnection[]; onChiudi: () => void; onFatto: () => void }) {
  const [cred, setCred] = useState(connessioni[0]?.id ?? '')
  const conn = connessioni.find((c) => c.id === cred)
  const cas = useAsync(() => (cred ? api.pkiCas(cred) : Promise.resolve(null)), [cred])
  const [config, setConfig] = useState<string>('')
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lavoro, setLavoro] = useState(false)
  useEffect(() => { setConfig(conn?.ca_config ?? cas.data?.cas[0]?.config ?? '') }, [conn, cas.data])
  async function vai() {
    setLavoro(true); setErrore(null)
    try {
      const r = await api.pkiSync(cred, config || null)
      setEsito(t('{a} nuovi, {u} già presenti', { a: r.added, u: r.updated }) + (r.truncated ? ` · ${t('elenco troncato: alza il limite o leggi più spesso')}` : ''))
    } catch (e) { setErrore(e) } finally { setLavoro(false) }
  }
  return (
    <Finestra titolo={t('Leggi i certificati emessi dalla CA')} sotto={t('Dal database della CA, con la connessione WinRM: i certificati emessi che scadono da 30 giorni fa in poi. Serve la lettura sulla CA (per esempio «Responsabile certificati»).')} onChiudi={onChiudi}>
      <ErrorBanner error={errore ?? cas.error} />
      <label className="field"><span className="field__label">{t('Connessione')}</span>
        <select value={cred} onChange={(e) => setCred(e.target.value)}>{connessioni.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
      <label className="field"><span className="field__label">{t('CA')}</span>
        <select value={config} onChange={(e) => setConfig(e.target.value)}>
          {(cas.data?.cas ?? []).filter((c) => c.config).map((c) => <option key={c.config!} value={c.config!}>{c.name} · {c.config}</option>)}
          {config && !(cas.data?.cas ?? []).some((c) => c.config === config) && <option value={config}>{config}</option>}
        </select></label>
      {cas.loading && <Spinner />}
      {esito && <p><strong>{esito}</strong></p>}
      <div className="toolbar" style={{ gap: 8 }}>
        <button type="button" disabled={!config || lavoro} onClick={vai}>{lavoro ? t('Lettura in corso…') : t('Leggi')}</button>
        {esito && <button type="button" className="secondary" onClick={onFatto}>{t('Fatto')}</button>}
      </div>
    </Finestra>
  )
}

// ------------------------------------------------------------ requests

function Richieste({ connessioni, onNuova }: { connessioni: PkiConnection[]; onNuova: () => void }) {
  const lista = useAsync(() => api.pkiRequests(), [])
  const [errore, setErrore] = useState<unknown>(null)
  const [occupato, setOccupato] = useState<string | null>(null)
  const nomeConn = useMemo(() => new Map(connessioni.map((c) => [c.id, c.name])), [connessioni])
  async function azione(id: string, f: () => Promise<unknown>) {
    setOccupato(id); setErrore(null)
    try { await f(); await lista.reload() } catch (e) { setErrore(e) } finally { setOccupato(null) }
  }
  const colonne: Column<PkiRequest>[] = [
    { key: 'cn', header: t('Certificato richiesto'), value: (r) => `${r.cn} ${r.sans.join(' ')}`, render: (r) => (
      <><strong>{r.cn || r.subject}</strong>{r.sans.length > 0 && <><br /><small className="text-muted">{r.sans.join(', ')}</small></>}
        <br /><small className="text-muted">{r.key_type}{r.key_bits ? ` ${r.key_bits}` : ''}</small></>
    ) },
    { key: 'ca', header: t('CA e modello'), value: (r) => `${r.ca_config ?? ''} ${r.template}`, render: (r) => (
      <><small>{nomeConn.get(r.credential_id ?? '') ?? '—'}</small><br /><small className="text-muted">{r.ca_config ? `${r.ca_config} · ` : ''}{r.template}</small></>
    ) },
    { key: 'status', header: t('Stato'), value: (r) => r.status, render: (r) => (
      <>
        <span className={`badge badge--${STATO_RICHIESTA[r.status]?.tono ?? 'muted'}`}>{STATO_RICHIESTA[r.status]?.label() ?? r.status}</span>
        {r.ca_request_id && <><br /><small className="text-muted">{t('richiesta n. {n}', { n: r.ca_request_id })}</small></>}
        {r.message && <><br /><small className="text-muted">{r.message}</small></>}
      </>
    ) },
    { key: 'created_at', header: t('Creata'), value: (r) => r.created_at ?? '', filter: false, render: (r) => (
      <><small>{data(r.created_at)}</small>{r.requested_by && <><br /><small className="text-muted">{r.requested_by}</small></>}</>
    ) },
    { key: 'azioni', header: '', filter: false, sort: false, render: (r) => (
      <div className="toolbar" style={{ gap: 6, flexWrap: 'wrap' }}>
        {(r.status === 'draft' || r.status === 'failed') && (
          <button type="button" disabled={occupato === r.id} onClick={() => void azione(r.id, () => api.pkiSubmit(r.id))}>{t('Invia alla CA')}</button>
        )}
        {r.status === 'pending' && (
          <button type="button" className="secondary" disabled={occupato === r.id} onClick={() => void azione(r.id, () => api.pkiRefresh(r.id))}>{t('Ricontrolla')}</button>
        )}
        {r.certificate_id && <Link to="/certificates?tab=repository">{t('Nel repository')}</Link>}
        {r.status !== 'pending' && (
          <button type="button" className="link" disabled={occupato === r.id}
            onClick={() => window.confirm(t('Eliminare la richiesta?')) && void azione(r.id, () => api.pkiDeleteRequest(r.id))}>{t('Elimina')}</button>
        )}
      </div>
    ) },
  ]
  return (
    <>
      <ErrorBanner error={errore ?? lista.error} />
      <div className="toolbar" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
        <button type="button" onClick={onNuova}>{t('Nuova richiesta')}</button>
      </div>
      {lista.loading && !lista.data ? <Spinner /> : (
        <Card>
          {(lista.data ?? []).length === 0 ? <EmptyState title={t('Nessuna richiesta')} hint={t('Le richieste si preparano in «Nuova richiesta», o con «Rinnova con la CA» da un certificato del repository o di Cifratura.')} />
            : <DataTable rows={lista.data!} rowKey={(r) => r.id} columns={colonne} initialSort={{ key: 'created_at', dir: 'desc' }}
              paginazione={{ predefinita: 50 }} />}
        </Card>
      )}
    </>
  )
}

function Copia({ testo }: { testo: string }) {
  const [fatto, setFatto] = useState(false)
  return (
    <button type="button" className="secondary" onClick={() => { void navigator.clipboard?.writeText(testo); setFatto(true); setTimeout(() => setFatto(false), 1500) }}>
      {fatto ? t('Copiato') : t('Copia')}
    </button>
  )
}

function NuovaRichiesta({ connessioni, iniziale, onCreata }: {
  connessioni: PkiConnection[]
  iniziale: { cn: string; sans: string; ci: string | null; replaces: string | null }
  onCreata: () => void
}) {
  const [cred, setCred] = useState(connessioni[0].id)
  const conn = connessioni.find((c) => c.id === cred)!
  const cas = useAsync(() => api.pkiCas(cred), [cred])
  const [config, setConfig] = useState('')
  const [modello, setModello] = useState('')
  const [cn, setCn] = useState(iniziale.cn)
  const [sans, setSans] = useState(iniziale.sans)
  const [chiave, setChiave] = useState('rsa2048')
  const [org, setOrg] = useState('')
  const [paese, setPaese] = useState('')
  const [ricetta, setRicetta] = useState<PkiRecipe | null>(null)
  const [csr, setCsr] = useState('')
  const [note, setNote] = useState('')
  const [errore, setErrore] = useState<unknown>(null)
  const [inviaSubito, setInviaSubito] = useState(true)
  const [lavoro, setLavoro] = useState(false)
  // Where the private key is born: on the server (the CSR comes back here) or
  // in CENSO, kept encrypted in the key store.
  const [dove, setDove] = useState<'server' | 'censo'>('server')

  useEffect(() => { setConfig(conn.ca_config ?? cas.data?.cas[0]?.config ?? '') }, [conn, cas.data])
  const ca = cas.data?.cas.find((c) => c.config === config) ?? cas.data?.cas[0]
  const modelli: PkiTemplate[] = (cas.data?.templates ?? []).filter((m) => !ca?.templates || ca.templates.includes(m.name))
  const scelto = modelli.find((m) => m.name === modello)

  async function prepara() {
    setErrore(null)
    try {
      setRicetta(await api.pkiRecipe({ cn, sans: sans.split(/[,\s]+/).filter(Boolean), key: chiave, organization: org, country: paese }))
    } catch (e) { setErrore(e) }
  }
  async function crea() {
    setLavoro(true); setErrore(null)
    try {
      const r = dove === 'censo' ? await api.pkiGenerate({
        credential_id: cred, template: modello, cn, sans: sans.split(/[,\s]+/).filter(Boolean), key: chiave,
        organization: org, country: paese, ca_config: conn.mode === 'winrm' ? config : null, ci_id: iniziale.ci,
        notes: note || null, replaces_sha256: iniziale.replaces,
      }) : await api.pkiCreateRequest({ credential_id: cred, template: modello, csr_pem: csr, ca_config: conn.mode === 'winrm' ? config : null,
        ci_id: iniziale.ci, notes: note || null, replaces_sha256: iniziale.replaces })
      if (inviaSubito && conn.write_enabled) await api.pkiSubmit(r.id)
      onCreata()
    } catch (e) { setErrore(e) } finally { setLavoro(false) }
  }
  function scaricaInf() {
    if (!ricetta) return
    void salva(new Blob([ricetta.windows_inf], { type: 'text/plain' }), `${ricetta.file_name}.inf`)
  }

  return (
    <>
      <ErrorBanner error={errore ?? cas.error} />
      {iniziale.replaces && <p className="field__hint">{t('Sostituisce il certificato con impronta {s}…', { s: iniziale.replaces.slice(0, 16) })}</p>}
      <Card title={t('1. CA e modello')}>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <label className="field"><span className="field__label">{t('Connessione')}</span>
            <select value={cred} onChange={(e) => { setCred(e.target.value); setModello('') }}>
              {connessioni.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.mode === 'web' ? 'Web Enrollment' : 'WinRM'}</option>)}
            </select></label>
          {conn.mode === 'winrm' && (
            <label className="field"><span className="field__label">{t('CA')}</span>
              <select value={config} onChange={(e) => setConfig(e.target.value)}>
                {(cas.data?.cas ?? []).filter((c) => c.config).map((c) => <option key={c.config!} value={c.config!}>{c.name} · {c.config}</option>)}
                {config && !(cas.data?.cas ?? []).some((c) => c.config === config) && <option value={config}>{config}</option>}
              </select></label>
          )}
          <label className="field"><span className="field__label">{t('Modello')}</span>
            <select value={modello} onChange={(e) => setModello(e.target.value)}>
              <option value="">{t('scegli…')}</option>
              {modelli.map((m) => <option key={m.name} value={m.name}>{m.display || m.name}{m.validity_days ? ` · ${t('{n} giorni', { n: m.validity_days })}` : ''}</option>)}
            </select></label>
        </div>
        {cas.loading && <Spinner />}
        {scelto && scelto.supplies_subject === false && (
          <p className="field__hint" style={{ color: 'var(--warn)' }}>{t('Questo modello costruisce il soggetto da Active Directory: i nomi della CSR vengono ignorati. Per un server web scegli un modello in cui il richiedente fornisce il soggetto (come «Server Web»).')}</p>
        )}
        {!conn.write_enabled && (
          <p className="field__hint" style={{ color: 'var(--warn)' }}>{t('La connessione non consente di inviare richieste: la richiesta resta in bozza finché un amministratore non accende «Consenti la scrittura» nella credenziale.')}</p>
        )}
      </Card>

      <Card title={t('2. Nomi e chiave')}>
        <div className="segmented" role="tablist" aria-label={t('Dove nasce la chiave')}>
          <button type="button" role="tab" aria-selected={dove === 'server'} className={dove === 'server' ? 'segmented__on' : ''} onClick={() => setDove('server')}>
            {t('Chiave sul server')}
          </button>
          <button type="button" role="tab" aria-selected={dove === 'censo'} className={dove === 'censo' ? 'segmented__on' : ''} onClick={() => setDove('censo')}>
            {t('Chiave generata in CENSO')}
          </button>
        </div>
        <p className="field__hint" style={{ marginTop: 0 }}>
          {dove === 'server'
            ? t('Indica i nomi del certificato: CENSO prepara i comandi da eseguire sul server. La chiave privata resta lì; qui torna solo la CSR. È la strada più sicura.')
            : t('CENSO genera chiave e CSR e tiene la chiave nello store, cifrata con la chiave del tenant: quando la CA emette il certificato, un amministratore lo scarica in PFX con la catena, pronto da installare.')}
        </p>
        <div className="row" style={{ gap: 12, flexWrap: 'wrap' }}>
          <label className="field"><span className="field__label">{t('Nome comune (CN)')}</span>
            <input type="text" value={cn} onChange={(e) => setCn(e.target.value)} placeholder={t('es. portale.dominio.local')} /></label>
          <label className="field" style={{ flex: 1, minWidth: 260 }}><span className="field__label">{t('Nomi alternativi (separati da virgola)')}</span>
            <input type="text" value={sans} onChange={(e) => setSans(e.target.value)} placeholder={t('es. www.dominio.local, 10.0.0.5')} /></label>
          <label className="field"><span className="field__label">{t('Chiave')}</span>
            <select value={chiave} onChange={(e) => setChiave(e.target.value)}>
              <option value="rsa2048">RSA 2048</option><option value="rsa3072">RSA 3072</option>
              <option value="rsa4096">RSA 4096</option><option value="ecdsa256">ECDSA P-256</option>
            </select></label>
          <label className="field"><span className="field__label">{t('Organizzazione')}</span><input type="text" value={org} onChange={(e) => setOrg(e.target.value)} maxLength={64} /></label>
          <label className="field"><span className="field__label">{t('Paese')}</span><input type="text" value={paese} onChange={(e) => setPaese(e.target.value.toUpperCase())} maxLength={2} style={{ width: 60 }} /></label>
        </div>
        {dove === 'server' && <button type="button" className="secondary" disabled={!cn.trim()} onClick={prepara}>{t('Prepara i comandi')}</button>}
        {dove === 'server' && ricetta && (
          <div style={{ marginTop: 12 }}>
            <h4 style={{ margin: '4px 0' }}>{t('Windows (certreq)')}</h4>
            <p className="field__hint" style={{ marginTop: 0 }}>{t('Salva il file .inf sul server, poi in un prompt da amministratore:')}</p>
            <div className="toolbar" style={{ gap: 8 }}><button type="button" className="secondary" onClick={scaricaInf}>{t('Scarica il file .inf')}</button><Copia testo={ricetta.windows_inf} /></div>
            <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{ricetta.windows_commands.slice(0, 2).join('\n')}</pre>
            <p className="field__hint">{t('Quando il certificato è emesso, scaricalo dal repository e completa sul server con:')} <span className="mono">{ricetta.windows_commands[2]}</span></p>
            <h4 style={{ margin: '12px 0 4px' }}>{t('Linux e altri (OpenSSL)')}</h4>
            <div className="toolbar" style={{ gap: 8 }}><Copia testo={ricetta.openssl_commands.join('\n')} /></div>
            <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{ricetta.openssl_commands.join('\n')}</pre>
          </div>
        )}
      </Card>

      <Card title={dove === 'server' ? t('3. Incolla la CSR e invia') : t('3. Genera e invia')}>
        {dove === 'server' && (
          <label className="field"><span className="field__label">{t('CSR (-----BEGIN CERTIFICATE REQUEST-----)')}</span>
            <textarea className="mono" rows={10} value={csr} onChange={(e) => setCsr(e.target.value)} /></label>
        )}
        <label className="field"><span className="field__label">{t('Note')}</span><input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} /></label>
        {conn.write_enabled && (
          <label className="check"><input type="checkbox" checked={inviaSubito} onChange={(e) => setInviaSubito(e.target.checked)} /> {t('Invia subito alla CA')}</label>
        )}
        <div className="toolbar" style={{ marginTop: 8 }}>
          <button type="button" disabled={(dove === 'server' ? !csr.trim() : !cn.trim()) || !modello || lavoro || (conn.mode === 'winrm' && !config)} onClick={crea}>
            {lavoro ? t('Invio in corso…') : inviaSubito && conn.write_enabled ? t('Crea e invia alla CA') : t('Salva la richiesta')}
          </button>
        </div>
      </Card>
    </>
  )
}

// ------------------------------------------------------------ CAs and templates

function CaEModelli({ connessioni }: { connessioni: PkiConnection[] }) {
  const [cred, setCred] = useState(connessioni[0].id)
  const cas = useAsync(() => api.pkiCas(cred), [cred])
  const colonne: Column<PkiTemplate>[] = [
    { key: 'display', header: t('Modello'), value: (m) => `${m.display} ${m.name}`, render: (m) => (
      <><strong>{m.display || m.name}</strong><br /><small className="mono text-muted">{m.name}</small></>
    ) },
    { key: 'supplies_subject', header: t('Soggetto dalla richiesta'), value: (m) => (m.supplies_subject == null ? '—' : m.supplies_subject ? t('sì') : t('no')) },
    { key: 'validity_days', header: t('Durata'), value: (m) => m.validity_days ?? 0, filter: false, render: (m) => (m.validity_days ? t('{n} giorni', { n: m.validity_days }) : '—') },
    { key: 'pubblicato', header: t('Pubblicato da'), value: (m) => (cas.data?.cas ?? []).filter((c) => c.templates?.includes(m.name)).map((c) => c.name).join(', '), filter: false },
  ]
  const colonneCa: Column<PkiCa>[] = [
    { key: 'name', header: t('CA'), value: (c) => `${c.name ?? ''} ${c.dns_host ?? ''}`.trim(), render: (c) => (
      <><strong>{c.name ?? '—'}</strong>{c.dns_host && <><br /><small className="text-muted">{c.dns_host}</small></>}</>
    ) },
    { key: 'config', header: t('Configurazione'), mono: true, value: (c) => c.config ?? '' },
    { key: 'ca_not_after', header: t('Certificato della CA'), type: 'date', value: (c) => c.ca_not_after ?? '', render: (c) => (
      <small>{c.ca_not_after ? t('valido fino al {d}', { d: data(c.ca_not_after) }) : '—'}</small>
    ) },
    { key: 'templates', header: t('Modelli pubblicati'), type: 'number', align: 'left', value: (c) => c.templates?.length ?? 0, render: (c) => <small>{c.templates?.length ?? 0}</small> },
  ]
  return (
    <>
      <div className="toolbar" style={{ gap: 8, marginBottom: 8 }}>
        <select value={cred} onChange={(e) => setCred(e.target.value)} style={{ maxWidth: 420 }}>{connessioni.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
        <button type="button" className="secondary" onClick={() => void cas.reload()}>{t('Aggiorna')}</button>
      </div>
      <ErrorBanner error={cas.error} />
      {cas.loading && !cas.data ? <Spinner /> : cas.data && (
        <>
          {cas.data.cas.some((c) => c.source === 'local') && (
            <p className="field__hint">
              {t('Nota: CA e modelli sono letti dalla CA installata sul server, perché dalla sessione WinRM Active Directory non è raggiungibile (con NTLM le credenziali non fanno il secondo salto verso il domain controller). Richieste, ritiro e certificati emessi funzionano normalmente; dei modelli si conosce solo il nome, senza durata né l’indicazione se il soggetto viene dalla richiesta.')}
              {cas.data.cas.find((c) => c.ad_error)?.ad_error && <><br /><small className="mono">{cas.data.cas.find((c) => c.ad_error)?.ad_error}</small></>}
            </p>
          )}
          <Card title={t('Autorità di certificazione')}>
            {cas.data.cas.length === 0 ? <EmptyState title={t('Nessuna CA aziendale trovata in Active Directory')} /> : (
              <DataTable rows={cas.data.cas} columns={colonneCa} rowKey={(c, i) => c.config ?? String(i)} />
            )}
          </Card>
          <Card title={t('Modelli di certificato')}>
            <DataTable rows={cas.data.templates} rowKey={(m) => m.name} columns={colonne} initialSort={{ key: 'display', dir: 'asc' }}
              paginazione={{ predefinita: 50 }} />
          </Card>
        </>
      )}
    </>
  )
}
